import { useCallback, useEffect, useId, useState } from "react";
import axios from "axios";
import { X } from "lucide-react";

type ModalRow = {
  placement?: string;
  link?: string;
  image?: string;
  key?: string;
  external_url?: string;
};

function normalizePublicidadList(data: unknown): ModalRow[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o.data)) return o.data as ModalRow[];
    if (Array.isArray(o.items)) return o.items as ModalRow[];
    if (Array.isArray(o.publicidad)) return o.publicidad as ModalRow[];
  }
  return [];
}

function isHttpUrl(s: string): boolean {
  return /^https?:\/\//i.test(s.trim());
}

const ModalPreview = () => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [imageBroken, setImageBroken] = useState(false);
  const titleId = useId();

  const close = useCallback(() => {
    setDismissed(true);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setImageBroken(false);
      setImageUrl(null);
      try {
        const base = process.env.REACT_APP_API_URL;
        if (!base) {
          console.error("REACT_APP_API_URL is not set");
          return;
        }

        const response = await axios.get(`${base}/api/publicidad/modal`);

        if (cancelled) return;

        const list = normalizePublicidadList(response.data);
        const row = list.find((i) => i.placement === "modalPictureOne");
        if (!row) {
          return;
        }

        const key = [row.link, row.image, row.key]
          .map((p) => (p == null ? "" : String(p).trim()))
          .find((s) => s.length > 0);
        const ext = row.external_url?.trim() ?? "";

        if (!key && isHttpUrl(ext)) {
          setImageUrl(ext);
          return;
        }

        if (key) {
          try {
            const { data: s3Data } = await axios.get(
              `${base}/api/s3/signed-url?key=${encodeURIComponent(key)}`,
              { headers: { "auth-token": sessionStorage.getItem("token") } }
            );
            if (cancelled) return;
            if (s3Data.url) {
              setImageUrl(s3Data.url);
              return;
            }
          } catch (err) {
            console.error("S3 signed-url error:", err);
          }
        }

        if (isHttpUrl(ext)) {
          setImageUrl(ext);
        } else {
          console.warn("Modal: modalPictureOne sin URL válida (link ni external_url).");
        }
      } catch (e) {
        console.error("Error fetching carousel modal:", e);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!imageUrl || dismissed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [imageUrl, dismissed, close]);

  const show = Boolean(imageUrl && !dismissed);

  if (loading || !show) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <button
        type="button"
        className="absolute inset-0 border-0 bg-black/50 backdrop-blur-sm"
        onClick={close}
        aria-label="Cerrar anuncio"
      />
      <div className="relative z-10 w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
        <button
          type="button"
          onClick={close}
          className="absolute top-2 right-2 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full border-0 bg-white/95 text-gray-800 shadow-md ring-1 ring-black/10 transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-[#205f43] focus:ring-offset-2"
          aria-label="Cerrar"
        >
          <X className="h-5 w-5" strokeWidth={2.5} />
        </button>
        <div className="p-0">
          <span id={titleId} className="sr-only">
            Anuncio
          </span>
          {imageBroken ? (
            <p className="p-6 text-center text-sm text-gray-600">
              No se pudo mostrar el anuncio. Intenta de nuevo más tarde.
            </p>
          ) : (
            <img
              src={imageUrl!}
              alt=""
              className="max-h-[min(85vh,900px)] w-full object-contain"
              onError={() => {
                setImageBroken(true);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalPreview;
